import { ApiClient } from '@api/api-configuration';
import FormElement from '@app/components/ui/FormElement';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { App, Input, InputNumber, Modal, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface RecordDepositModalProps {
  open: boolean;
  caregiverId: number | null;
  caregiverName?: string;
  /** Current cash-on-hand; prefilled as the default deposit amount. */
  outstandingAmount?: number;
  onClose: () => void;
  /** Called after a successful deposit so the caller can refresh the list. */
  onSaved: () => void;
}

const RecordDepositModal = ({
  open,
  caregiverId,
  caregiverName,
  outstandingAmount,
  onClose,
  onSaved,
}: RecordDepositModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { handleError } = useSmartErrorHandling();

  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Seed the form whenever the modal opens: default the amount to the caregiver's
  // full outstanding cash (editable down for partial deposits).
  useEffect(() => {
    if (!open) return;
    setAmount(outstandingAmount ?? null);
    setNote('');
    setError('');
  }, [open, caregiverId, outstandingAmount]);

  const handleSave = async () => {
    if (caregiverId == null) return;

    if (amount == null || amount <= 0) {
      setError(t('caregiver-cash-deposit-amount-invalid'));
      return;
    }

    try {
      setIsSaving(true);
      await ApiClient.billingApi.billing.recordCaregiverCashDeposit(caregiverId, {
        amount,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      message.success(t('caregiver-cash-deposit-success'));
      onSaved();
      onClose();
    } catch (e) {
      handleError(e as Error, { customMessage: t('caregiver-cash-deposit-failed') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('caregiver-cash-deposit-title')}
      onCancel={isSaving ? undefined : onClose}
      onOk={handleSave}
      okText={t('common-save')}
      cancelText={t('common-cancel')}
      confirmLoading={isSaving}
      maskClosable={!isSaving}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3 pt-2">
        {caregiverName && (
          <Typography.Text type="secondary">
            {t('caregiver-cash-deposit-caregiver', { name: caregiverName })}
          </Typography.Text>
        )}

        <FormElement label={t('caregiver-cash-deposit-amount-label')}>
          <InputNumber
            size="large"
            className="w-full"
            min={0}
            value={amount}
            disabled={isSaving}
            onChange={(value) => {
              setAmount(value ?? null);
              setError('');
            }}
          />
        </FormElement>

        <FormElement label={t('caregiver-cash-deposit-note-label')}>
          <Input.TextArea
            rows={3}
            value={note}
            disabled={isSaving}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('caregiver-cash-deposit-note-placeholder')}
          />
        </FormElement>

        {error && <div className="text-red-500 text-xs">{error}</div>}
      </div>
    </Modal>
  );
};

export default RecordDepositModal;
